import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AppModal from "../../components/AppModal";
import Pagination from "../../components/Pagination/Pagination";
import FilterBar, { FilterField } from "../../components/FilterBar";
import { useListController } from "../../controllers/useListController";
import { submissionsService, formsService } from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { showToast } from "../../utils/toast";
import { formatDateTime } from "../../utils/date";
import { takePageFilter, peekPageFilter } from "../../utils/listNavigation";

const STATUS_FILTERS = [
  { id: "all", label: "All submissions" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

const statusParam = (filterId) => (filterId === "all" ? "" : filterId);
const VALID_STATUS_FILTERS = STATUS_FILTERS.map((f) => f.id);

export default function Submissions() {
  const location = useLocation();
  const { rows, pagination, loading, error, fetchRows } =
    useListController(submissionsService);
  const { can } = usePermission();
  const canUpdate = can("submissions", "is_update");

  const [forms, setForms] = useState([]);
  const [viewFilter, setViewFilter] = useState("all");
  const [formCode, setFormCode] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const queryParams = (extra = {}) => ({
    page: extra.page ?? pagination.page,
    limit: extra.limit ?? pagination.limit,
    status: statusParam(extra.filter ?? extra.status ?? viewFilter),
    form_code: extra.form_code ?? formCode,
    search: extra.search ?? search,
  });

  useEffect(() => {
    formsService.getAll({}).then((res) => setForms(res?.data?.list || []));
  }, []);

  useEffect(() => {
    const incoming =
      peekPageFilter("/submissions") ?? location.state?.filter ?? null;
    if (incoming && VALID_STATUS_FILTERS.includes(incoming)) {
      takePageFilter("/submissions");
      setViewFilter(incoming);
    }
  }, [location.key, location.state]);

  useEffect(() => {
    fetchRows({ page: 1, status: statusParam(viewFilter), form_code: formCode, search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter, formCode]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchRows({ page: 1, status: statusParam(viewFilter), form_code: formCode, search });
  };

  const handleStatusChange = (e) => {
    setViewFilter(e.target.value);
    setSearch("");
  };

  const handleFormChange = (e) => {
    setFormCode(e.target.value);
    setSearch("");
  };

  const onReset = (e) => {
    e.preventDefault();
    setViewFilter("all");
    setFormCode("");
    setSearch("");
    fetchRows({ page: 1, status: "", form_code: "", search: "" });
  };

  const emptyMessage =
    viewFilter === "pending"
      ? "No pending submissions"
      : viewFilter === "approved"
        ? "No approved submissions"
        : viewFilter === "rejected"
          ? "No rejected submissions"
          : "No submissions";

  const openDetail = (row) => {
    setDetail(row);
    setNote(row.admin_note || "");
  };

  const isEmptyValue = (v) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

  const formatLabel = (key) => {
    const m = key.match(/^(.*?)[_]?(\d+)$/);
    let base = key;
    let idx = null;
    if (m) {
      base = m[1];
      idx = Number(m[2]) + 1;
    }
    base = base.replace(/_/g, " ").trim();
    return idx != null ? `${base} ${idx}` : base;
  };

  const formatValue = (v) => {
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "object" && v !== null) return JSON.stringify(v);
    return String(v);
  };

  const payloadDuplicatesMeta = (key, value, detail) => {
    const val = formatValue(value);
    const kl = String(key).toLowerCase();
    if (kl === "centre" && detail.centre && val === detail.centre) return true;
    if (/^child(_name)?(_\d+)?$/.test(kl) && detail.child_name && val === detail.child_name) return true;
    if (/^(account_name|family_name|parent_name)$/.test(kl) && detail.family_name && val === detail.family_name) {
      return true;
    }
    return false;
  };

  const getSubmittedDetailRows = (detail) => {
    if (!detail) return [];
    const rows = [];
    const push = (label, value) => {
      if (isEmptyValue(value)) return;
      rows.push({ label, value: formatValue(value) });
    };

    push("Family", detail.family_name);
    push("Centre", detail.centre);
    push("Child", detail.child_name);
    push("Submitted", formatDateTime(detail.submitted_at));

    Object.entries(detail.payload || {})
      .filter(([, v]) => !isEmptyValue(v))
      .filter(([k, v]) => !payloadDuplicatesMeta(k, v, detail))
      .forEach(([k, v]) => push(formatLabel(k), v));

    return rows;
  };

  const statusBadge = (status) => {
    if (status === "approved") return <span className="badge bg-success">Approved</span>;
    if (status === "rejected") return <span className="badge bg-danger">Rejected</span>;
    return <span className="badge bg-warning text-dark">Pending</span>;
  };

  const setStatusFor = async (row, newStatus) => {
    const res = await submissionsService.updateStatus(row.id, newStatus, note);
    if (res?.success) {
      showToast("success", `Marked ${newStatus}`);
      setDetail(null);
      fetchRows(queryParams());
    } else {
      showToast("error", res?.msg || "Failed");
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header">
        <strong>Form Submissions</strong>
      </div>

      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        <FilterBar onSubmit={onSearch} onReset={onReset}>
          <FilterField id="sub-status-filter" label="Show">
            <select
              id="sub-status-filter"
              className="form-select"
              value={viewFilter}
              onChange={handleStatusChange}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id="sub-form-filter" label="Form">
            <select
              id="sub-form-filter"
              className="form-select"
              value={formCode}
              onChange={handleFormChange}
            >
              <option value="">All forms</option>
              {forms.map((f) => (
                <option key={f.code} value={f.code}>{f.name}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id="sub-search" label="Search">
            <input
              id="sub-search"
              className="form-control"
              placeholder="Reference, family, child…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Form</th>
                <th>Family / Child</th>
                <th>Centre</th>
                <th>Submitted</th>
                <th>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} className="text-center py-4">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-4 text-muted">{emptyMessage}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="fw-semibold">{r.reference_no}</td>
                    <td>{r.form_name}</td>
                    <td>
                      <div>{r.family_name || "—"}</div>
                      <div className="small text-muted">{r.child_name || ""}</div>
                    </td>
                    <td>{r.centre || "—"}</td>
                    <td className="small text-muted">{formatDateTime(r.submitted_at)}</td>
                    <td>{statusBadge(r.status)}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => openDetail(r)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          itemsCount={rows.length}
          loading={loading}
          onPageChange={(page) => fetchRows({ ...queryParams(), page })}
          onLimitChange={(limit) => fetchRows({ ...queryParams(), page: 1, limit })}
        />
      </div>

      <AppModal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `${detail.form_name} — ${detail.reference_no}` : ""}
        size="lg"
        footer={
          <>
            {canUpdate && detail?.status === "pending" && (
              <>
                <button className="btn btn-success" onClick={() => setStatusFor(detail, "approved")}>
                  Approve
                </button>
                <button className="btn btn-danger" onClick={() => setStatusFor(detail, "rejected")}>
                  Reject
                </button>
              </>
            )}
            {canUpdate && detail?.status !== "pending" && (
              <button className="btn btn-outline-warning" onClick={() => setStatusFor(detail, "pending")}>
                Re-open (Pending)
              </button>
            )}
            <button type="button" className="btn btn-outline-secondary" onClick={() => setDetail(null)}>Close</button>
          </>
        }
      >
              {detail && (
                <>
              <p className="bw-payload-section">Submitted details</p>
              {(() => {
                const rows = getSubmittedDetailRows(detail);
                return (
                  <div className="bw-payload">
                    {rows.length > 0 ? (
                      <div className="bw-payload-grid">
                        {rows.map((row, i) => (
                          <div className="bw-payload-row" key={`${row.label}-${i}`}>
                            <div className="bw-payload-label">{row.label}</div>
                            <div className="bw-payload-value">{row.value}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted mb-0 px-3 py-3">No additional details.</p>
                    )}
                  </div>
                );
              })()}

              {canUpdate && (
                <div className="bw-payload-note">
                  <label className="form-label">Internal note (optional)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              )}
                </>
              )}
      </AppModal>
    </div>
  );
}
